import React, { useCallback } from "react";

import { TagsContentProps } from "./content.model";
import { Button } from "@companion-core/shared/app/Components/Button";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { Tag } from "@companion-core/shared/app/Components/Tags/Tag";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { useOnboardingStore } from "@companion-core/shared/app/Store/onboardingStore";
import { useMountEffect } from "@companion-core/shared/app/Hooks/useMountEffect";
import {
  trackPreferencesClick,
  trackPreferencesSkipClick,
  trackPreferencesPage,
} from "@companion-core/shared/app/tracking/blocks";
import { getCloudinaryOptimizedUrl } from "@companion-core/shared/app/Utils/cloudinary";

import "@companion-core/web/src/Assets/Styles/scss/universesLayout.scss";

export const Tags = ({ t, universe, goBack, skipAction, onSubmitTags }: TagsContentProps) => {
  const isMobileScreen = useMediaQuery("(max-width: 768px)");
  const { tags: selectedTags, setTags } = useOnboardingStore();

  const toggleTag = useCallback(
    (slug: string) => {
      const currentTags = selectedTags ?? [];
      const nextTags = currentTags.includes(slug)
        ? currentTags.filter((currentSlug) => currentSlug !== slug)
        : [...currentTags, slug];
      setTags(nextTags.length ? nextTags : undefined);
    },
    [selectedTags, setTags]
  );

  useMountEffect(() => {
    trackPreferencesPage({ universeSlug: universe.slug });
  });

  return (
    <section className="universes-layout__tags-section">
      {!isMobileScreen && (
        <div className="universes-layout__side-panel">
          <button
            type="button"
            className="universes-layout__side-panel-go-back-button"
            data-testid="universes-layout__button--go-back"
            onClick={goBack}>
            <FontIcon name="JP-Arrow-left-bold" size={34} />
            <span className="universes-layout__side-panel-header-text title-7">
              {t("blocks.universes.tags.goBack")}
            </span>
          </button>
          <img
            className="universes-layout__side-panel-image"
            src={getCloudinaryOptimizedUrl(universe.medias?.[0]?.url ?? "", {
              contentType: "image",
              width: 720,
            })}
            alt={universe.name}
          />
        </div>
      )}
      <div className="universes-layout__tags-content">
        {isMobileScreen && (
          <div>
            <button
              type="button"
              className="universes-layout__tags-content-go-back-button"
              data-testid="universes-layout__button--go-back"
              onClick={goBack}>
              <FontIcon name="JP-Arrow-left-bold" size={24} />
              <span className="universes-layout__tags-content-go-back-button-text body-text-7">
                {t("blocks.universes.tags.goBack")}
              </span>
            </button>
          </div>
        )}

        <div className="universes-layout__tags-content-container">
          <div className="universes-layout__tags-content-header">
            <h1 className="universes-layout__tags-content-title title-7">
              {t("blocks.universes.tags.title")}
            </h1>
            <p className="universes-layout__tags-content-description body-text-6">
              {t("blocks.universes.tags.description")}
            </p>
          </div>
          {universe.tag_categories?.map((tagCategory) => (
            <div key={tagCategory.id} className="universes-layout__tags-content-category">
              <h2 className="universes-layout__tags-content-category-title body-text-3">
                {tagCategory.title}
              </h2>
              <div className="universes-layout__tags-content-category-tags">
                {tagCategory.tags?.map((tag) => (
                  <Tag
                    key={tag.slug}
                    tag={tag.title}
                    slug={tag.slug}
                    type="secondary"
                    selected={selectedTags?.includes(tag.slug)}
                    onClick={() => toggleTag(tag.slug)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="universes-layout__tags-content-footer">
          <Button
            label={t("blocks.universes.tags.next")}
            testID="universes-layout__button--tags-next"
            type="primary"
            size={isMobileScreen ? "full" : "large"}
            onClick={() => {
              trackPreferencesClick({ universeSlug: universe.slug, tags: selectedTags?.join("|") });
              onSubmitTags();
            }}
          />
          <Button
            label={t("blocks.universes.skip")}
            testID="universes-layout__button--see-all"
            type="ghost"
            size={isMobileScreen ? "full" : "large"}
            onClick={() => {
              trackPreferencesSkipClick({ universeSlug: universe.slug });
              skipAction();
            }}
          />
        </div>
      </div>
    </section>
  );
};
